import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fmt } from "@/lib/format";
import { resolveImageUrl } from "@/components/ImageUpload";

const SUGAR = ["0%", "25%", "50%", "75%", "100%"];
const ICE = ["No Ice", "Less Ice", "Regular", "Extra Ice"];

export default function ModifierModal({ item, toppings, onClose, onConfirm }) {
  const sizes = item.sizes && item.sizes.length > 0 ? item.sizes : [{ name: "Regular", price_delta: 0 }];
  const editing = item._editing;

  const [size, setSize] = useState(editing?.size || sizes[0].name);
  const [sugar, setSugar] = useState(editing?.sugar || "100%");
  const [ice, setIce] = useState(editing?.ice || "Regular");
  const [selectedToppings, setSelectedToppings] = useState(editing?.toppings || []);
  const [qty, setQty] = useState(editing?.quantity || 1);
  const [notes, setNotes] = useState(editing?.notes || "");

  useEffect(() => {
    if (!editing) {
      setSize(sizes[0].name);
      setSugar("100%");
      setIce("Regular");
      setSelectedToppings([]);
      setQty(1);
      setNotes("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const sizeObj = sizes.find((s) => s.name === size) || sizes[0];
  const toppingsTotal = selectedToppings.reduce((s, t) => s + t.price, 0);
  const unitPrice = +(item.base_price + sizeObj.price_delta + toppingsTotal).toFixed(2);
  const lineTotal = +(unitPrice * qty).toFixed(2);

  const toggleTopping = (t) => {
    const exists = selectedToppings.find((x) => x.id === t.id);
    if (exists) {
      setSelectedToppings(selectedToppings.filter((x) => x.id !== t.id));
    } else {
      setSelectedToppings([...selectedToppings, { id: t.id, name: t.name, price: t.price }]);
    }
  };

  const confirm = () => {
    onConfirm({
      menu_item_id: item.id,
      name: item.name,
      size,
      sugar,
      ice,
      toppings: selectedToppings,
      unit_price: unitPrice,
      quantity: qty,
      line_total: lineTotal,
      notes,
    });
  };

  const availableToppings = useMemo(
    () => toppings.filter((t) => t.available && t.stock > 0),
    [toppings]
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="modifier-modal">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr]">
          <div className="bg-boba-muted aspect-square md:aspect-auto">
            {item.image_url ? (
              <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-7xl">🥤</div>
            )}
          </div>
          <div className="p-6 max-h-[80vh] overflow-y-auto scroll-thin">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{item.name}</DialogTitle>
              <div className="text-sm text-boba-inkSoft">{item.description}</div>
            </DialogHeader>

            <div className="mt-5 space-y-4">
              {sizes.length > 1 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2">Size</div>
                  <div className="flex flex-wrap gap-2" data-testid="size-options">
                    {sizes.map((s) => (
                      <button
                        key={s.name}
                        onClick={() => setSize(s.name)}
                        className={`chip ${size === s.name ? "active" : ""}`}
                        data-testid={`size-${s.name.toLowerCase()}`}
                      >
                        {s.name} {s.price_delta > 0 ? `+${fmt(s.price_delta)}` : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2">Sugar</div>
                <div className="flex flex-wrap gap-2" data-testid="sugar-options">
                  {SUGAR.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSugar(s)}
                      className={`chip ${sugar === s ? "active" : ""}`}
                      data-testid={`sugar-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2">Ice</div>
                <div className="flex flex-wrap gap-2" data-testid="ice-options">
                  {ICE.map((s) => (
                    <button
                      key={s}
                      onClick={() => setIce(s)}
                      className={`chip ${ice === s ? "active" : ""}`}
                      data-testid={`ice-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2">Toppings</div>
                <div className="flex flex-wrap gap-2" data-testid="topping-options">
                  {availableToppings.map((t) => {
                    const active = !!selectedToppings.find((x) => x.id === t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleTopping(t)}
                        className={`chip ${active ? "active" : ""}`}
                        data-testid={`topping-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {t.name} <span className="font-mono opacity-70 ml-1">+{fmt(t.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2">Notes</div>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Special instructions…"
                  className="w-full h-10 px-3 border border-boba-stoneLine rounded-md text-sm focus:outline-none focus:border-boba-matcha"
                  data-testid="notes-input"
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Qty</div>
                <div className="flex items-center border border-boba-stoneLine rounded-md">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-9 h-9 hover:bg-stone-100"
                    data-testid="qty-minus"
                  >−</button>
                  <div className="w-10 text-center font-mono font-semibold" data-testid="qty-value">{qty}</div>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-9 h-9 hover:bg-stone-100"
                    data-testid="qty-plus"
                  >+</button>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-boba-stoneLine flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-boba-inkSoft uppercase tracking-widest font-mono">Total</div>
                <div className="font-mono text-2xl font-semibold text-boba-matcha" data-testid="modifier-total">
                  {fmt(lineTotal)}
                </div>
              </div>
              <button
                onClick={confirm}
                className="flex-1 max-w-[200px] h-12 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-semibold transition-colors"
                data-testid="modifier-confirm"
              >
                {editing ? "Update" : "Add to Order"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
